'use client';

import { Dialog as SheetPrimitive } from '@base-ui/react/dialog';
import type * as React from 'react';
import { CloseButton } from '@/components/ui/close-button';
import { cn } from '@/lib/utils';

// Base UI starting/ending styles let native transitions reverse from the
// current presentation without restarting keyframes or changing modal lifecycle.
export const sheetBackdropMotion =
  'motion-reduce:transition-none motion-safe:transition-opacity motion-safe:duration-(--duration-slow) motion-safe:data-closed:duration-(--duration-moderate) motion-safe:ease-(--ease-decelerate) motion-safe:data-closed:ease-(--ease-exit) motion-safe:data-starting-style:opacity-0 motion-safe:data-ending-style:opacity-0';
export const sheetPanelMotion =
  'motion-reduce:transition-none motion-safe:transition-transform motion-safe:duration-(--duration-slow) motion-safe:data-closed:duration-(--duration-moderate) motion-safe:ease-(--ease-decelerate) motion-safe:data-closed:ease-(--ease-exit) motion-safe:data-[side=left]:data-starting-style:-translate-x-full motion-safe:data-[side=left]:data-ending-style:-translate-x-full motion-safe:data-[side=right]:data-starting-style:translate-x-full motion-safe:data-[side=right]:data-ending-style:translate-x-full motion-safe:data-[side=top]:data-starting-style:-translate-y-full motion-safe:data-[side=top]:data-ending-style:-translate-y-full motion-safe:data-[side=bottom]:data-starting-style:translate-y-full motion-safe:data-[side=bottom]:data-ending-style:translate-y-full';

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

export interface SheetOverlayProps extends SheetPrimitive.Backdrop.Props {
  /** Disables open/close animations */
  disableAnimation?: boolean;
  /** Disables backdrop blur effect */
  disableBackdropBlur?: boolean;
}

function SheetOverlay({
  className,
  disableAnimation,
  disableBackdropBlur,
  ...props
}: SheetOverlayProps) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        'bg-foreground/10 fixed inset-0 z-50',
        !disableAnimation && sheetBackdropMotion,
        !disableBackdropBlur && 'supports-backdrop-filter:backdrop-blur-xs',
        className,
      )}
      {...props}
    />
  );
}

export interface SheetContentProps extends SheetPrimitive.Popup.Props {
  side?: 'top' | 'right' | 'bottom' | 'left';
  showCloseButton?: boolean;
  /** Disables open/close animations */
  disableAnimation?: boolean;
  /** Disables backdrop blur effect */
  disableBackdropBlur?: boolean;
}

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  disableAnimation,
  disableBackdropBlur,
  ...props
}: SheetContentProps) {
  return (
    <SheetPortal>
      <SheetOverlay
        disableAnimation={disableAnimation}
        disableBackdropBlur={disableBackdropBlur}
      />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          'bg-background fixed z-50 flex flex-col gap-4 bg-clip-padding text-sm shadow-lg data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm',
          !disableAnimation && sheetPanelMotion,
          // end-4 + size-8 close target + gap-4.
          showCloseButton &&
            '[:where(&>[data-slot=sheet-header]:not(.sr-only))]:pe-16',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            render={<CloseButton className="absolute top-4 end-4" />}
          />
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  // Defaults and popup clearance stay below consumer utilities; retain sr-only geometry.
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        'gap-1.5 [:where(&)]:p-4 [&.sr-only]:p-4 flex flex-col',
        className,
      )}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('gap-2 p-4 mt-auto flex flex-col', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-foreground font-medium', className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

SheetTrigger.displayName = 'Sheet.Trigger';
SheetClose.displayName = 'Sheet.Close';
SheetPortal.displayName = 'Sheet.Portal';
SheetOverlay.displayName = 'Sheet.Overlay';
SheetContent.displayName = 'Sheet.Content';
SheetHeader.displayName = 'Sheet.Header';
SheetFooter.displayName = 'Sheet.Footer';
SheetTitle.displayName = 'Sheet.Title';
SheetDescription.displayName = 'Sheet.Description';
Sheet.displayName = 'Sheet';

const SheetNamespace = Object.assign(Sheet, {
  Trigger: SheetTrigger,
  Close: SheetClose,
  Portal: SheetPortal,
  Overlay: SheetOverlay,
  Content: SheetContent,
  Header: SheetHeader,
  Footer: SheetFooter,
  Title: SheetTitle,
  Description: SheetDescription,
});

export {
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetNamespace as Sheet,
  SheetTitle,
  SheetTrigger,
};
